import { IOrcamento } from "app/types/IOrcamento"

// 482572 — regras de habilitação dos campos de DESCONTO do Totalizador, extraídas de
// TotalizadorContainer.tsx para um módulo puro (sem React/redux) porque agora existem DOIS
// consumidores das mesmas regras:
//
//   1) TotalizadorContainer.tsx — monta as props `desativar*` do <Totalizador>;
//   2) app/redux/store/store.ts — decide quais campos de desconto entram no snapshot de
//      "orçamento em andamento" (só os HABILITADOS, ver extrairSnapshotOrcamentoEmAndamento).
//
// Manter as expressões num único lugar é o ponto do módulo: se a regra de habilitação mudar e o
// snapshot continuar com a regra antiga, a recuperação volta a gravar desconto num campo que a
// tela não deixa editar (ou o desconto geral junto com o nacional/importado, dobrando o valor).
// As expressões abaixo são as MESMAS que estavam inline nas props, sem alteração de comportamento.

export type ContextoCamposDesconto = {
    orcamento: IOrcamento
    /** totalizadorSlice.desabilitarEdicoes */
    edicoesDesabilitadas: boolean
    valorParametro: (nome: string) => string | undefined
    /** parâmetro por item, resolvido para a filial do orçamento (OidFilialOrcamento) */
    valorParametroPorFilial: (nome: string) => string | undefined
}

export type DesativacaoCamposDesconto = {
    percentualDesconto: boolean
    valorDesconto: boolean
    percentualDescontoMercadoriasNacionais: boolean
    valorDescontoMercadoriasNacionais: boolean
    percentualDescontoMercadoriasImportadas: boolean
    valorDescontoMercadoriasImportadas: boolean
    percentualDescontoAcumulado: boolean
}

export function desativacaoCamposDesconto(contexto: ContextoCamposDesconto): DesativacaoCamposDesconto {
    const orcamento = contexto.orcamento
    const valorParametro = contexto.valorParametro
    const edicoesDesabilitadas = contexto.edicoesDesabilitadas
    const orcamentoSemItens = (orcamento.ItensOrcamento ?? []).length === 0

    return {
        percentualDesconto: orcamentoSemItens || edicoesDesabilitadas || (valorParametro("APLICAR_PERCENTUAL_DESCONTO_SOBRE_PRECO_UNITARIO") == "S" && valorParametro("DESCONTO_ACUMULADO") == "S") ? true : valorParametro("DESCONTO_ACUMULADO") == "S" ? false : orcamento.TipoTratamentoDesconto === 0,
        valorDesconto: orcamentoSemItens || edicoesDesabilitadas || (valorParametro("APLICAR_PERCENTUAL_DESCONTO_SOBRE_PRECO_UNITARIO") == "S" && valorParametro("DESCONTO_ACUMULADO") == "S") ? true : valorParametro("DESCONTO_ACUMULADO") == "S" ? false : orcamento.TipoTratamentoDesconto === 1,

        percentualDescontoMercadoriasNacionais: orcamentoSemItens || edicoesDesabilitadas || orcamento.ValorTotalMercadoriasNacionais <= 0 || (valorParametro("APLICAR_PERCENTUAL_DESCONTO_SOBRE_PRECO_UNITARIO") == "S" && valorParametro("DESCONTO_ACUMULADO") == "S") ? true : valorParametro("DESCONTO_ACUMULADO") == "S" ? false : orcamento.TipoTratamentoDesconto === 0,
        valorDescontoMercadoriasNacionais: orcamentoSemItens || edicoesDesabilitadas || orcamento.ValorTotalMercadoriasNacionais <= 0 || (valorParametro("APLICAR_PERCENTUAL_DESCONTO_SOBRE_PRECO_UNITARIO") == "S" && valorParametro("DESCONTO_ACUMULADO") == "S") ? true : valorParametro("DESCONTO_ACUMULADO") == "S" ? false : orcamento.TipoTratamentoDesconto === 1,

        percentualDescontoMercadoriasImportadas: orcamentoSemItens || edicoesDesabilitadas || orcamento.ValorTotalMercadoriasImportadas <= 0 || (valorParametro("APLICAR_PERCENTUAL_DESCONTO_SOBRE_PRECO_UNITARIO") == "S" && valorParametro("DESCONTO_ACUMULADO") == "S") ? true : valorParametro("DESCONTO_ACUMULADO") == "S" ? false : orcamento.TipoTratamentoDesconto === 0,
        valorDescontoMercadoriasImportadas: orcamentoSemItens || edicoesDesabilitadas || orcamento.ValorTotalMercadoriasImportadas <= 0 || (valorParametro("APLICAR_PERCENTUAL_DESCONTO_SOBRE_PRECO_UNITARIO") == "S" && valorParametro("DESCONTO_ACUMULADO") == "S") ? true : valorParametro("DESCONTO_ACUMULADO") == "S" ? false : orcamento.TipoTratamentoDesconto === 1,

        percentualDescontoAcumulado: orcamentoSemItens || edicoesDesabilitadas,
    }
}

// Nomes de campo de IOrcamento/OrcamentoEmAndamento (não os nomes das props `desativar*`) — este é
// o mapa consumido pelo snapshot, que grava por campo do orçamento.
export type CamposDescontoHabilitados = {
    PercentualDesconto: boolean
    ValorDesconto: boolean
    PercentualDescontoSobreMercadoriasNacionais: boolean
    ValorDescontoSobreMercadoriasNacionais: boolean
    PercentualDescontoSobreMercadoriasImportadas: boolean
    ValorDescontoSobreMercadoriasImportadas: boolean
    AplicaDescontoCashback: boolean
}

// 482572 — "habilitado" aqui é o que o usuário de fato consegue editar na tela: além do
// `desativar*` correspondente, entra o que o próprio Totalizador.tsx aplica por cima —
// somente leitura (orçamento em modo leitura ou vindo de DAV), desconto de ICMS de órgão público
// (`ValorDescontoIcms > 0` trava todos os campos de desconto) e, principalmente, a VISIBILIDADE:
// com PERMITE_DESDOBRAR_PEDIDO_PRODUTO_IMPORTADO = "S" o Totalizador troca o desconto geral pelos
// pares Nacional/Importado — o campo geral nem é renderizado, então seu valor não pode ser tratado
// como uma edição do usuário a ser recuperada (recuperá-lo junto dobraria o desconto).
export function camposDescontoHabilitados(contexto: ContextoCamposDesconto): CamposDescontoHabilitados {
    const orcamento = contexto.orcamento
    const desativado = desativacaoCamposDesconto(contexto)

    // mesma expressão de `somenteLeitura` em Totalizador.tsx (globalMU.isNotNullOrEmpty(CodigoDAV)),
    // reescrita sem globalMU para o módulo continuar puro (é importado por store.ts).
    const somenteLeitura = orcamento.SomenteLeitura === true
        || (orcamento.CodigoDAV != null && String(orcamento.CodigoDAV).trim() !== "")
    const temDescontoIcms = (orcamento.ValorDescontoIcms ?? 0) > 0
    const separarNacionalImportado = contexto.valorParametroPorFilial("PERMITE_DESDOBRAR_PEDIDO_PRODUTO_IMPORTADO") == "S"

    const habilitado = (exibido: boolean, campoDesativado: boolean) =>
        exibido && !campoDesativado && !somenteLeitura && !temDescontoIcms

    // checkbox "Aplicar Desconto Cashback": só existe na tela quando o cliente tem saldo/uso de
    // cashback (ver Totalizador.tsx) e é travado pelo mesmo `desativarPercentualDescontoAcumulado`.
    const exibeCashback = (orcamento.CodigoCliente ?? 0) > 7
        && ((orcamento.SaldoCashbackCliente ?? 0) > 0 || (orcamento.ValorCashbackUtilizado ?? 0) > 0)

    return {
        PercentualDesconto: habilitado(!separarNacionalImportado, desativado.percentualDesconto),
        ValorDesconto: habilitado(!separarNacionalImportado, desativado.valorDesconto),
        PercentualDescontoSobreMercadoriasNacionais: habilitado(separarNacionalImportado, desativado.percentualDescontoMercadoriasNacionais),
        ValorDescontoSobreMercadoriasNacionais: habilitado(separarNacionalImportado, desativado.valorDescontoMercadoriasNacionais),
        PercentualDescontoSobreMercadoriasImportadas: habilitado(separarNacionalImportado, desativado.percentualDescontoMercadoriasImportadas),
        ValorDescontoSobreMercadoriasImportadas: habilitado(separarNacionalImportado, desativado.valorDescontoMercadoriasImportadas),
        AplicaDescontoCashback: exibeCashback && !desativado.percentualDescontoAcumulado && !somenteLeitura,
    }
}
